// src/pages/auth/RegisterScreen.jsx
// ─────────────────────────────────────────────────────────────
// Registration Flow:
//
// Step 1 – Enter company + first admin user details
// Step 2 – Verify email OTP
// Step 3 – Create company + first admin user
//
// On registration:
//   company_master  → company details
//   company_user    → first user / admin
//
// First user:
//   CU_Role     = "admin"
//   CU_Is_Owner = 1
//   CU_Status   = 1
//
// No device ID is used.
// No CM_User_1 is used.
// No CM_Device_1/2/3 are used.
// ─────────────────────────────────────────────────────────────

import { useState, useRef } from "react";
import { registerAPI, storeCompany } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../../assets/logo.png";

export default function RegisterScreen({ onBack }) {

  const { loginWithCompany } = useAuth();

  const [step, setStep] = useState(1); // 1=details, 2=otp

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const [otp, setOtp] = useState([
    "",
    "",
    "",
    "",
    "",
    ""
  ].slice(0, 6));

  const serverOtpRef = useRef(null);

  /*
  |--------------------------------------------------------------------------
  | Registration Form
  |--------------------------------------------------------------------------
  */

  const [form, setForm] = useState({
    userName: "",
    shopName: "",
    phone: "",
    address: "",
    city: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  /*
  |--------------------------------------------------------------------------
  | Form Setter
  |--------------------------------------------------------------------------
  */

  const setF = (key) => (e) => {

    setForm(f => ({
      ...f,
      [key]: e.target.value
    }));

    setError("");

    /*
    | If email changes, previous OTP is no longer valid.
    */

    if (key === "email") {
      setOtpSent(false);
      setOtpVerified(false);
      serverOtpRef.current = null;
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Validate Form
  |--------------------------------------------------------------------------
  */

  const validate = () => {

    if (!form.userName.trim()) {
      return "Please enter your name.";
    }

    if (!form.shopName.trim()) {
      return "Please enter the shop name.";
    }

    if (!form.phone.trim()) {
      return "Please enter a phone number.";
    }

    if (!/^\d{10}$/.test(form.phone.trim())) {
      return "Please enter a valid 10-digit phone number.";
    }

    if (!form.address.trim()) {
      return "Please enter the shop address.";
    }

    if (!form.city.trim()) {
      return "Please enter the city.";
    }

    if (!form.email.trim()) {
      return "Please enter the email address.";
    }

    if (!form.email.includes("@")) {
      return "Please enter a valid email.";
    }

    if (!form.password) {
      return "Please enter a password.";
    }

    if (form.password.length < 6) {
      return "Password must be at least 6 characters long.";
    }

    if (!form.confirmPassword) {
      return "Please confirm your password.";
    }

    if (form.password !== form.confirmPassword) {
      return "Passwords do not match.";
    }

    return null;
  };

  /*
  |--------------------------------------------------------------------------
  | Send OTP
  |--------------------------------------------------------------------------
  */

  const handleSendOTP = async () => {

    const err = validate();

    if (err) {
      setError(err);
      return;
    }

    setOtpSending(true);
    setError("");

    try {

      const res = await registerAPI.sendEmailOTP(
        form.email.trim()
      );

      if (res.status !== "success") {

        setError(
          res.message ?? "Failed to send OTP."
        );

        return;
      }

      serverOtpRef.current = String(
        res.otp ?? ""
      );

      setOtpSent(true);
      setStep(2);

    } catch (e) {

      // setError(
      //   "Network error. Please check your connection."
      // );

      console.error(
        "[RegisterScreen] sendOTP:",
        e
      );

    } finally {

      setOtpSending(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Verify OTP
  |--------------------------------------------------------------------------
  */

  const handleVerifyOTP = () => {

    const entered = otp.join("");

    if (entered.length < 6) {

      setError(
        "Please enter the full 6-digit OTP."
      );

      return;
    }

    if (
      serverOtpRef.current &&
      entered !== serverOtpRef.current
    ) {

      setError(
        "Incorrect OTP. Please try again."
      );

      return;
    }

    setError("");
    setOtpVerified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Create Company + First Admin User
  |--------------------------------------------------------------------------
  */

  const handleCreateCompany = async () => {

    if (!otpVerified) {

      setError(
        "Please verify your email OTP first."
      );

      return;
    }

    setLoading(true);
    setError("");

    try {

      /*
      |--------------------------------------------------------------------------
      | IMPORTANT
      |--------------------------------------------------------------------------
      | No device ID is sent anymore.
      |
      | The registration API receives:
      |
      | Company:
      |   CM_Name
      |   CM_Address
      |   CM_City
      |   CM_ContactNo
      |   CM_Email
      |
      | First User:
      |   CU_Name
      |   CU_Email
      |   CU_Password
      |
      | Backend automatically creates:
      |   CU_Role     = admin
      |   CU_Is_Owner = 1
      |   CU_Status   = 1
      |--------------------------------------------------------------------------
      */

      const res = await registerAPI.createCompany({

        // Company details
        companyName: form.shopName.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        contactNo: form.phone.trim(),
        email: form.email.trim(),

        // First user / admin details
        userName: form.userName.trim(),
        userEmail: form.email.trim(),
        password: form.password,
      });

      /*
      |--------------------------------------------------------------------------
      | API Error
      |--------------------------------------------------------------------------
      */

      if (res.status !== "success") {

        setError(
          res.message ??
          "Failed to create shop. Please try again."
        );

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | Build Company Object
      |--------------------------------------------------------------------------
      */

      const company = {

        CM_Id:
          res.data?.CM_ID,

        CM_Name:
          res.data?.CM_Name ??
          form.shopName,

        CM_Email:
          res.data?.CM_Email ??
          form.email,

        CM_Address:
          res.data?.CM_Address ??
          form.address,

        CM_City:
          res.data?.CM_City ??
          form.city,

        CM_ContactNo:
          res.data?.CM_ContactNo ??
          form.phone,

        CM_Logo:
          res.data?.CM_Logo ??
          "",

        CM_Status:
          1,
      };

      /*
      |--------------------------------------------------------------------------
      | Store Company
      |--------------------------------------------------------------------------
      */

      storeCompany(company);

      /*
      |--------------------------------------------------------------------------
      | Login as First Admin
      |--------------------------------------------------------------------------
      */

      loginWithCompany(company);
      navigate("/welcome", { replace: true });

    } catch (e) {

      // setError(
      //   "Network error. Please check your connection."
      // );

      console.error(
        "[RegisterScreen] createCompany:",
        e
      );

    } finally {

      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | OTP Input Helpers
  |--------------------------------------------------------------------------
  */

  const otpRefs = Array.from(
    { length: 6 },
    () => useRef(null)
  );

  const handleOtpChange = (i, val) => {

    if (!/^\d*$/.test(val)) {
      return;
    }

    const next = [...otp];

    next[i] = val.slice(-1);

    setOtp(next);

    setOtpVerified(false);
    setError("");

    if (val && i < 5) {
      otpRefs[i + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (i, e) => {

    if (
      e.key === "Backspace" &&
      !otp[i] &&
      i > 0
    ) {
      otpRefs[i - 1].current?.focus();
    }

    if (e.key === "Enter") {
      handleVerifyOTP();
    }
  };

  const handleOtpPaste = (e) => {

    const text = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (text.length === 6) {

      setOtp(text.split(""));

      otpRefs[5].current?.focus();
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="auth-page">

      <div
        className="auth-card"
        style={{ maxWidth: 460 }}
      >

        {/* Logo */}

        <div className="auth-logo">

          <img
            src={logo}
            alt=""
            className="auth-logo-icon"
          />

          <h1 className="auth-logo-title">
            TailorExpert
          </h1>

          <p className="auth-logo-sub">
            Register your tailoring shop
          </p>

        </div>


        {/* Step tabs */}

        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 24,
            alignItems: "center"
          }}
        >

          {[
            {
              n: 1,
              label: "Shop Details"
            },
            {
              n: 2,
              label: "Verify Email"
            }
          ].map((s, i) => (

            <div
              key={s.n}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1
              }}
            >

              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,

                  background:
                    step >= s.n
                      ? "var(--gold)"
                      : "var(--surface2)",

                  color:
                    step >= s.n
                      ? "var(--bg)"
                      : "var(--text-dim)",

                  border:
                    step >= s.n
                      ? "none"
                      : "1px solid var(--border)",
                }}
              >

                {step > s.n
                  ? "✓"
                  : s.n}

              </div>

              <span
                style={{
                  fontSize: 12.5,

                  color:
                    step >= s.n
                      ? "var(--text)"
                      : "var(--text-dim)",

                  fontWeight:
                    step === s.n
                      ? 600
                      : 400
                }}
              >
                {s.label}
              </span>

              {i < 1 && (

                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background:
                      step > 1
                        ? "var(--gold)"
                        : "var(--border)"
                  }}
                />

              )}

            </div>

          ))}

        </div>


        {/* Error */}

        {error && (

          <div
            className="error-msg"
            style={{
              marginBottom: 14
            }}
          >
            {error}
          </div>

        )}


        {/* =====================================================
            STEP 1
            ===================================================== */}

        {step === 1 && (

          <div className="auth-form">

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12
              }}
            >

              {/* Your Name */}

              <div
                className="form-group"
                style={{
                  gridColumn: "1 / -1"
                }}
              >

                <label className="form-label">

                  Your Name{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  placeholder="e.g. Ramesh Patel"
                  value={form.userName}
                  onChange={setF("userName")}
                  autoFocus
                />

                <span
                  style={{
                    fontSize: 11.5,
                    color: "var(--text-dim)",
                    marginTop: 5,
                    display: "block"
                  }}
                >
                  You will be registered as the
                  company administrator.
                </span>

              </div>


              {/* Shop Name */}

              <div
                className="form-group"
                style={{
                  gridColumn: "1 / -1"
                }}
              >

                <label className="form-label">

                  Shop Name{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  placeholder="e.g. Mehta Tailors"
                  value={form.shopName}
                  onChange={setF("shopName")}
                />

              </div>


              {/* Phone */}

              <div className="form-group">

                <label className="form-label">

                  Phone Number{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  placeholder="10-digit mobile"
                  value={form.phone}
                  onChange={setF("phone")}
                  maxLength={10}
                  inputMode="numeric"
                />

              </div>


              {/* City */}

              <div className="form-group">

                <label className="form-label">

                  City{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  placeholder="e.g. Rajkot"
                  value={form.city}
                  onChange={setF("city")}
                />

              </div>


              {/* Address */}

              <div
                className="form-group"
                style={{
                  gridColumn: "1 / -1"
                }}
              >

                <label className="form-label">

                  Shop Address{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  placeholder="Street, locality..."
                  value={form.address}
                  onChange={setF("address")}
                />

              </div>


              {/* Email */}

              <div
                className="form-group"
                style={{
                  gridColumn: "1 / -1"
                }}
              >

                <label className="form-label">

                  Email Address{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  type="email"
                  placeholder="shop@email.com"
                  value={form.email}
                  onChange={setF("email")}
                />

                <span
                  style={{
                    fontSize: 11.5,
                    color: "var(--text-dim)",
                    marginTop: 5,
                    display: "block"
                  }}
                >
                  This email will be used for
                  login and email verification.
                </span>

              </div>


              {/* Password */}

              <div className="form-group">

                <label className="form-label">

                  Password{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  type="password"
                  placeholder="Create password"
                  value={form.password}
                  onChange={setF("password")}
                  autoComplete="new-password"
                />

              </div>


              {/* Confirm Password */}

              <div className="form-group">

                <label className="form-label">

                  Confirm Password{" "}

                  <span className="form-required">
                    *
                  </span>

                </label>

                <input
                  className="form-input"
                  type="password"
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={setF("confirmPassword")}
                  autoComplete="new-password"
                />

              </div>

            </div>


            {/* Buttons */}

            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 4
              }}
            >

              <button
                className="btn btn-ghost"
                onClick={onBack}
                style={{
                  flex: 1
                }}
              >
                ← Back
              </button>

              <button
                className="btn btn-primary btn-lg"
                onClick={handleSendOTP}
                disabled={otpSending}
                style={{
                  flex: 2
                }}
              >

                {otpSending ? (
                  <>
                    <span
                      style={spinnerStyle}
                    />
                    Sending OTP…
                  </>
                ) : (
                  "Send OTP to Email →"
                )}

              </button>

            </div>

          </div>

        )}


        {/* =====================================================
            STEP 2 - OTP
            ===================================================== */}

        {step === 2 && (

          <div className="auth-form">

            {/* Summary */}

            <div
              style={{
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "10px 14px",
                marginBottom: 20,
                fontSize: 13,
                color: "var(--text-muted)"
              }}
            >

              <div
                style={{
                  fontWeight: 700,
                  color: "var(--text)",
                  marginBottom: 4
                }}
              >
                🏪 {form.shopName}
              </div>

              <div>
                👤 {form.userName}
              </div>

              <div>
                📧 OTP sent to{" "}
                <strong
                  style={{
                    color: "var(--gold)"
                  }}
                >
                  {form.email}
                </strong>
              </div>

            </div>


            {/* OTP Label */}

            <label
              className="form-label"
              style={{
                display: "block",
                marginBottom: 12
              }}
            >

              Enter 6-digit OTP{" "}

              <span className="form-required">
                *
              </span>

            </label>


            {/* OTP Inputs */}

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "center",
                marginBottom: 16
              }}
              onPaste={handleOtpPaste}
            >

              {otp.map((digit, i) => (

                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e =>
                    handleOtpChange(
                      i,
                      e.target.value
                    )
                  }
                  onKeyDown={e =>
                    handleOtpKeyDown(i, e)
                  }
                  autoFocus={i === 0}
                  style={{
                    width: 44,
                    height: 50,
                    textAlign: "center",
                    fontSize: 20,
                    fontWeight: 700,

                    background:
                      otpVerified
                        ? "var(--green-dim)"
                        : "var(--surface2)",

                    border:
                      `2px solid ${
                        otpVerified
                          ? "var(--green)"
                          : digit
                            ? "var(--gold)"
                            : "var(--border)"
                      }`,

                    borderRadius: 8,
                    color: "var(--text)",
                    outline: "none"
                  }}
                />

              ))}

            </div>


            {/* Verify */}

            {!otpVerified ? (

              <button
                className="btn btn-ghost btn-full"
                onClick={handleVerifyOTP}
                disabled={
                  otp.join("").length < 6
                }
                style={{
                  marginBottom: 12
                }}
              >
                ✔ Verify OTP
              </button>

            ) : (

              <div
                style={{
                  background: "var(--green-dim)",
                  border:
                    "1px solid rgba(76,175,122,0.3)",
                  borderRadius:
                    "var(--radius-sm)",
                  padding: "9px 14px",
                  textAlign: "center",
                  color: "var(--green)",
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 12
                }}
              >
                ✅ Email verified successfully!
              </div>

            )}


            {/* Create Account */}

            <div
              style={{
                display: "flex",
                gap: 8
              }}
            >

              <button
                className="btn btn-ghost"
                onClick={() => {

                  setStep(1);

                  setOtp([
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                  ]);

                  setOtpVerified(false);

                }}
                style={{
                  flex: 1
                }}
              >
                ← Back
              </button>


              <button
                className="btn btn-primary btn-lg"
                onClick={handleCreateCompany}
                disabled={
                  !otpVerified ||
                  loading
                }
                style={{
                  flex: 2,
                  opacity:
                    otpVerified
                      ? 1
                      : 0.45
                }}
              >

                {loading ? (
                  <>
                    <span
                      style={spinnerStyle}
                    />
                    Creating Account…
                  </>
                ) : (
                  "🎉 Create Account"
                )}

              </button>

            </div>


            {/* Resend */}

            <div
              style={{
                textAlign: "center",
                marginTop: 14
              }}
            >

              <span
                style={{
                  fontSize: 12.5,
                  color: "var(--gold)",
                  cursor: "pointer",
                  fontWeight: 600
                }}
                onClick={() => {

                  setStep(1);

                  setOtp([
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                  ]);

                  setOtpVerified(false);
                  setOtpSent(false);
                  serverOtpRef.current = null;

                }}
              >
                Didn't receive OTP? Resend
              </span>

            </div>

          </div>

        )}

      </div>

    </div>
  );
}


/*
|--------------------------------------------------------------------------
| Spinner
|--------------------------------------------------------------------------
*/

const spinnerStyle = {
  display: "inline-block",
  width: 14,
  height: 14,
  border: "2px solid rgba(255,255,255,0.3)",
  borderTop: "2px solid #fff",
  borderRadius: "50%",
  animation: "spin 0.7s linear infinite",
  marginRight: 6
};